import React, { useState } from 'react';
import { C, R, SHADOW, fmt } from '../tokens.js';
import { Card, Btn, BkHdr, Badge, Chip, SPill, SHead, ImgOrEmoji } from '../components/ui.jsx';
import { D } from '../mock-data.js';
import { listFeaturedBranches } from '../api/branches.js';
import { listNewProducts } from '../api/products.js';
import { getRecommendations } from '../api/recommendations.js';
import { useApi } from '../lib/hooks.js';
import { nextDelivery, dayAbbrev } from '../lib/schedule.js';
import { DEFAULT_LOCATION, findCity } from '../lib/cities.js';

/* ── Category config ────────────────────────────────────────────
   live:true  → shows branch list
   live:false → shows "coming soon" screen
   btype      → key in D.branches
   filterFn   → optional predicate on branches
   subCats    → chip labels shown inside the feed
   ─────────────────────────────────────────────────────────────── */
export const CATEGORIES = [
  {id:'grocery',  label:'Grocery',    em:'🛒', bg:'#dcfce7', live:true,  btype:'grocery', filterFn:null,                                                       subCats:['All','Dairy','Bakery','Vegetables','Poultry','Grocery']},
  {id:'dairy',    label:'Dairy',      em:'🥛', bg:'#dbeafe', live:true,  btype:'grocery', filterFn:b=>b.type.includes('Dairy'),                                subCats:['All','Milk','Curd','Paneer','Ghee','Butter']},
  {id:'bakery',   label:'Bakery',     em:'🍞', bg:'#fef3c7', live:true,  btype:'grocery', filterFn:b=>b.type.includes('Bakery'),                               subCats:['All','Bread','Pastry','Cakes','Snacks']},
  {id:'produce',  label:'Produce',    em:'🥬', bg:'#dcfce7', live:true,  btype:'grocery', filterFn:b=>b.type.includes('Produce')||b.type.includes('Organic'),  subCats:['All','Vegetables','Fruits','Herbs','Sprouts']},
  {id:'salons',   label:'Salons',     em:'✂️', bg:'#fce7f3', live:true,  btype:'salons',  filterFn:null,                                                       subCats:['All','Unisex','Men','Women','Premium']},
  {id:'dining',   label:'Dining',     em:'🍽️', bg:'#d4f1f7', live:false, btype:null,      filterFn:null, subCats:[]},
  {id:'pharmacy', label:'Pharmacy',   em:'💊', bg:'#f3e8ff', live:false, btype:null,      filterFn:null, subCats:[]},
  {id:'laundry',  label:'Laundry',    em:'🧺', bg:'#e0f2fe', live:false, btype:null,      filterFn:null, subCats:[]},
  {id:'petcare',  label:'Pet Care',   em:'🐾', bg:'#fef3c7', live:false, btype:null,      filterFn:null, subCats:[]},
  {id:'cleaning', label:'Cleaning',   em:'🧹', bg:'#dcfce7', live:false, btype:null,      filterFn:null, subCats:[]},
  {id:'wellness', label:'Wellness',   em:'🧘', bg:'#fce7f3', live:false, btype:null,      filterFn:null, subCats:[]},
  {id:'fitness',  label:'Fitness',    em:'🏋️', bg:'#dbeafe', live:false, btype:null,      filterFn:null, subCats:[]},
];

export const getBranches = (cat) => {
  if(!cat.live||!cat.btype) return [];
  const base = D.branches[cat.btype]||[];
  return cat.filterFn ? base.filter(cat.filterFn) : base;
};

/* ── Featured stores (editorial, admin-curated) ───────────────── */
const FeaturedStoreCard = ({branch, onOpen}) => {
  const bg = branch.bg || '#d4f1f7';
  return (
    <button onClick={()=>onOpen(branch)} style={{flexShrink:0,width:208,padding:0,background:C.white,borderRadius:R.xl,border:`1px solid ${C.border}`,boxShadow:SHADOW.brandCard,overflow:'hidden',cursor:'pointer',textAlign:'left',scrollSnapAlign:'start'}}>
      <div style={{height:104,background:`linear-gradient(135deg,${bg},#f1fbfd)`,position:'relative',overflow:'hidden'}}>
        <ImgOrEmoji src={D.storeImages[branch.id]} fallback={branch.em} size={104} radius={0} bg="transparent" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
        <div style={{position:'absolute',top:10,left:10}}>
          <span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'3px 9px',borderRadius:R.pill,background:C.primary,color:C.white,fontSize:10,fontWeight:800,letterSpacing:.4,boxShadow:'0 2px 6px rgba(12,192,223,.30)'}}>★ FEATURED</span>
        </div>
        {branch.certified&&(
          <div style={{position:'absolute',top:10,right:10}}>
            <Badge bg="rgba(255,255,255,.92)" color="#15803d" sm>✓</Badge>
          </div>
        )}
      </div>
      <div style={{padding:'12px 14px'}}>
        <p style={{fontSize:13,fontWeight:800,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',letterSpacing:-.1,lineHeight:1.3}}>{branch.name}</p>
        <p style={{fontSize:11,color:C.textMed,marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',lineHeight:1.3}}>{branch.type}</p>
        <div style={{display:'flex',alignItems:'center',gap:6,marginTop:8}}>
          <svg width="11" height="11" viewBox="0 0 12 12" fill="#d97706"><polygon points="6,1 7.5,4.5 11.5,5 8.5,7.5 9.5,11.5 6,9.5 2.5,11.5 3.5,7.5 0.5,5 4.5,4.5"/></svg>
          <span style={{fontSize:11,fontWeight:800,color:C.text,fontVariantNumeric:'tabular-nums'}}>{branch.rating}</span>
          <span style={{width:2,height:2,borderRadius:1,background:C.textLight}}/>
          <span style={{fontSize:11,color:C.textMed,fontVariantNumeric:'tabular-nums'}}>{branch.dist} km</span>
        </div>
      </div>
    </button>
  );
};

const FeaturedRow = ({navigate}) => {
  const {data, error, loading} = useApi(() => listFeaturedBranches({limit: 6}), []);
  // Soft section: hide on empty/error so a stale backend doesn't break Home.
  if (loading) return null;
  if (error || !data || data.length === 0) return null;
  const isSalon = (b) => D.branches.salons.some(s => s.id === b.id);
  return (
    <div>
      <SHead title="Featured for your routine"/>
      <div style={{display:'flex',gap:12,overflowX:'auto',margin:'0 -16px',padding:'2px 16px 6px',scrollSnapType:'x mandatory',WebkitOverflowScrolling:'touch'}}>
        {data.map(b => (
          <FeaturedStoreCard key={b.id} branch={b} onOpen={(branch)=>navigate('branch',{branch,btype:isSalon(branch)?'salons':'grocery'})}/>
        ))}
      </div>
    </div>
  );
};

/* ── Routine surfaces ────────────────────────────────────────── */
const formatDeliveryDate = (date) => {
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate()+1);
  const d = new Date(date); d.setHours(0,0,0,0);
  if (d.getTime() === today.getTime()) return 'Today';
  if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
  return `${dayAbbrev(d)}, ${d.getDate()} ${d.toLocaleString('en-IN',{month:'short'})}`;
};

const NextDeliveryCard = ({delivery, count, onViewRoutine}) => (
  <button onClick={onViewRoutine} style={{width:'100%',padding:0,background:'none',border:'none',cursor:'pointer',textAlign:'left'}}>
    <div style={{padding:'16px 16px 18px',background:`linear-gradient(135deg,${C.primary} 0%,${C.accent} 100%)`,borderRadius:R.xl,boxShadow:SHADOW.brandCard,position:'relative',overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10}}>
        <span style={{fontSize:10,fontWeight:800,color:'rgba(255,255,255,.85)',letterSpacing:.6}}>NEXT DELIVERY</span>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:14}}>
        <div style={{width:54,height:54,borderRadius:R.lg,background:'rgba(255,255,255,.18)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:30,flexShrink:0,lineHeight:1}}>{delivery.row.productEm}</div>
        <div style={{flex:1,minWidth:0}}>
          <p style={{fontSize:17,fontWeight:800,color:C.white,letterSpacing:-.2,lineHeight:1.2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{delivery.row.productName} · {delivery.row.variantLabel}</p>
          <p style={{fontSize:12,color:'rgba(255,255,255,.85)',marginTop:3,lineHeight:1.4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{formatDeliveryDate(delivery.date)} · {delivery.row.deliveryWindow} · from {delivery.row.branchName}</p>
        </div>
      </div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:14,paddingTop:12,borderTop:'1px solid rgba(255,255,255,.18)'}}>
        <span style={{fontSize:12,fontWeight:700,color:'rgba(255,255,255,.92)',letterSpacing:.1}}>{count>1?`+${count-1} more on this day`:'View your routine'}</span>
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M7 4l6 6-6 6" stroke="rgba(255,255,255,.95)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
    </div>
  </button>
);

const GetStartedCard = ({onStart}) => (
  <button onClick={onStart} style={{width:'100%',padding:0,background:'none',border:'none',cursor:'pointer',textAlign:'left'}}>
    <div style={{padding:'18px 16px 18px',background:`linear-gradient(135deg,${C.primary} 0%,${C.accent} 100%)`,borderRadius:R.xl,boxShadow:SHADOW.brandCard,position:'relative',overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
        <div style={{width:48,height:48,borderRadius:R.lg,background:'rgba(255,255,255,.18)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,flexShrink:0,lineHeight:1}}>📅</div>
        <div style={{flex:1,minWidth:0}}>
          <p style={{fontSize:17,fontWeight:800,color:C.white,letterSpacing:-.2,lineHeight:1.25}}>Set up your daily routine</p>
          <p style={{fontSize:12,color:'rgba(255,255,255,.85)',marginTop:3,lineHeight:1.4}}>Milk, bread, eggs — delivered before you wake up</p>
        </div>
      </div>
      <div style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 14px',background:C.white,borderRadius:R.pill,fontSize:13,fontWeight:800,color:C.primary,letterSpacing:.1}}>
        Browse groceries
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M7 4l6 6-6 6" stroke={C.primary} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
    </div>
  </button>
);

const RoutineChips = ({subs, navigate}) => {
  const active = subs.filter(s => s.status === 'active');
  if (active.length === 0) return null;
  return (
    <div>
      <SHead title="Your routine"/>
      <div style={{display:'flex',gap:8,overflowX:'auto',margin:'0 -16px',padding:'2px 16px 4px',scrollSnapType:'x mandatory',WebkitOverflowScrolling:'touch'}}>
        {active.map(s => (
          <button key={s.id} onClick={()=>navigate('sub-detail',{sub:s})} style={{flexShrink:0,display:'flex',alignItems:'center',gap:8,padding:'8px 12px 8px 8px',background:C.white,border:`1px solid ${C.border}`,borderRadius:R.pill,cursor:'pointer',scrollSnapAlign:'start',minHeight:40,boxShadow:'0 1px 2px rgba(28,25,23,.04)'}}>
            <div style={{width:24,height:24,borderRadius:12,background:'#e6f7fb',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,lineHeight:1,flexShrink:0}}>{s.productEm||'🛒'}</div>
            <span style={{fontSize:12,fontWeight:700,color:C.text,letterSpacing:.05}}>{s.product}</span>
            <span style={{fontSize:11,color:C.textMed}}>· {s.cadence} {s.window}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/* Horizontal scroller of recently-launched subscribable products. */
const NewOnRekraftRail = ({navigate}) => {
  const { data } = useApi(() => listNewProducts({ limit: 8, sinceDays: 14 }), []);
  if (!data || data.length < 2) return null;
  return (
    <div>
      <SHead title="🆕 New on Rekraft"/>
      <div style={{display:'flex',gap:10,overflowX:'auto',margin:'0 -16px',padding:'2px 16px 6px',scrollSnapType:'x mandatory',WebkitOverflowScrolling:'touch'}}>
        {data.map(p => (
          <button key={p.id} onClick={()=>navigate('product',{product:p,branch:{id:p.branchId,name:p.branchName,rating:p.branchRating}})} style={{flexShrink:0,width:148,padding:0,background:C.white,borderRadius:R.xl,border:`1px solid ${C.border}`,boxShadow:SHADOW.card,overflow:'hidden',cursor:'pointer',textAlign:'left',scrollSnapAlign:'start'}}>
            <div style={{height:80,background:'linear-gradient(135deg,#e6f7fb,#d4f1f7)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:36,lineHeight:1}}>{p.em}</div>
            <div style={{padding:'10px 12px'}}>
              <p style={{fontSize:12,fontWeight:800,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',letterSpacing:-.1}}>{p.name}</p>
              <p style={{fontSize:10,color:C.textMed,marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>🏬 {p.branchName}</p>
              <p style={{fontSize:11,fontWeight:800,color:C.primary,marginTop:6}}>from {fmt(p.variants?.[0]?.p || 0)}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/* "You might like" — inventory-rule recommendations on Home, gated on
 * having at least one active subscription. */
const YouMightLikeRail = ({subscriptions, navigate}) => {
  const activeItems = subscriptions.filter(s => s.status === 'active').map(s => ({ productId: s.productId, id: s.productId }));
  const branchId = subscriptions.find(s => s.status === 'active')?.branchId;
  const { data } = useApi(
    () => getRecommendations({ context: 'home_routine_upsell', branchId, items: activeItems, limit: 6 }),
    [subscriptions.length, branchId],
  );
  if (subscriptions.filter(s => s.status === 'active').length === 0) return null;
  if (!data || data.length === 0) return null;
  return (
    <div>
      <SHead title="You might like"/>
      <div style={{display:'flex',gap:10,overflowX:'auto',margin:'0 -16px',padding:'2px 16px 6px',scrollSnapType:'x mandatory',WebkitOverflowScrolling:'touch'}}>
        {data.map(r => (
          <button key={r.id} onClick={()=>{
            const branch = D.branches.grocery.find(x => x.id === r.branchId);
            const product = branch?.products?.find(x => x.id === r.id);
            if (branch && product) navigate('product',{product,branch});
          }} style={{flexShrink:0,width:148,padding:0,background:C.white,borderRadius:R.xl,border:`1px solid ${C.border}`,boxShadow:SHADOW.card,overflow:'hidden',cursor:'pointer',textAlign:'left',scrollSnapAlign:'start'}}>
            <div style={{height:80,background:'linear-gradient(135deg,#dcfce7,#f1fbfd)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:36,lineHeight:1}}>{r.em}</div>
            <div style={{padding:'10px 12px'}}>
              <p style={{fontSize:12,fontWeight:800,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',letterSpacing:-.1}}>{r.name}</p>
              <p style={{fontSize:10,color:C.textMed,marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>🏬 {r.branchName}</p>
              <p style={{fontSize:11,fontWeight:800,color:C.primary,marginTop:6}}>from {fmt(r.pricePaise)}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const NearbyServicesRow = ({navigate}) => {
  const salons = D.branches.salons || [];
  if (salons.length === 0) return null;
  return (
    <div>
      <SHead title="Nearby services" action="See all" onAction={()=>navigate('category-feed',{cat:CATEGORIES.find(c=>c.id==='salons')})}/>
      <div style={{display:'flex',gap:10,overflowX:'auto',margin:'0 -16px',padding:'2px 16px 6px',scrollSnapType:'x mandatory',WebkitOverflowScrolling:'touch'}}>
        {salons.map(s => (
          <button key={s.id} onClick={()=>navigate('branch',{branch:s,btype:'salons'})} style={{flexShrink:0,width:168,padding:0,background:C.white,borderRadius:R.lg,border:`1px solid ${C.border}`,boxShadow:'0 1px 2px rgba(28,25,23,.04)',overflow:'hidden',cursor:'pointer',textAlign:'left',scrollSnapAlign:'start'}}>
            <div style={{height:72,background:`linear-gradient(135deg,${s.bg||'#fce7f3'},#f1fbfd)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:30,lineHeight:1}}>{s.em}</div>
            <div style={{padding:'10px 12px'}}>
              <p style={{fontSize:12,fontWeight:800,color:C.text,letterSpacing:-.1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{s.name}</p>
              <p style={{fontSize:10,color:C.textMed,marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{s.type}</p>
              <div style={{display:'flex',alignItems:'center',gap:5,marginTop:6}}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="#d97706"><polygon points="6,1 7.5,4.5 11.5,5 8.5,7.5 9.5,11.5 6,9.5 2.5,11.5 3.5,7.5 0.5,5 4.5,4.5"/></svg>
                <span style={{fontSize:10,fontWeight:800,color:C.text,fontVariantNumeric:'tabular-nums'}}>{s.rating}</span>
                <span style={{fontSize:10,color:C.textMed,fontVariantNumeric:'tabular-nums'}}>· {s.dist} km</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ── Address picker bottom sheet ──────────────────────────────── */
const tagEmoji = (label) => {
  if (label === 'Home') return '🏠';
  if (label === 'Office') return '🏢';
  return '📍';
};

const AddressPickerSheet = ({ open, onClose, addresses, onPick, onAddNew }) => {
  if (!open) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60, display: 'flex', alignItems: 'flex-end' }} onClick={onClose}>
      <div style={{ background: C.white, borderRadius: '20px 20px 0 0', width: '100%', maxHeight: '70%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 0' }}><div style={{ width: 40, height: 4, borderRadius: 2, background: C.border }}/></div>
        <div style={{ padding: '14px 20px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: -0.2 }}>Deliver to</p>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 20px' }}>
          {addresses.length === 0 && (
            <p style={{ fontSize: 13, color: C.textMed, textAlign: 'center', padding: '24px 0' }}>No saved addresses yet. Add one below.</p>
          )}
          {addresses.map((a) => {
            const active = a.isDefault;
            return (
              <button key={a.id} onClick={() => onPick(a)} style={{ width: '100%', display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', background: active ? '#e6f7fb' : C.white, border: `1.5px solid ${active ? C.primary : C.border}`, borderRadius: R.lg, marginBottom: 8, cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: active ? C.white : C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0, lineHeight: 1 }}>{tagEmoji(a.label)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{a.label}</span>
                    {active && <Badge bg="#dcfce7" color="#15803d" sm>Active</Badge>}
                  </div>
                  <p style={{ fontSize: 11, color: C.textMed, lineHeight: 1.45 }}>{a.full}</p>
                </div>
                <div style={{ width: 18, height: 18, borderRadius: 9, border: `2px solid ${active ? C.primary : '#d6d3d1'}`, background: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 8 }}>
                  {active && <div style={{ width: 9, height: 9, borderRadius: 5, background: C.primary }}/>}
                </div>
              </button>
            );
          })}
          <button onClick={onAddNew} style={{ width: '100%', padding: '12px 14px', background: C.white, border: `1.5px dashed ${C.primary}`, borderRadius: R.lg, color: C.primary, fontSize: 13, fontWeight: 800, cursor: 'pointer', marginTop: 4 }}>
            + Add new address
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Home Screen ─────────────────────────────────────────────── */
export const HomeScreen = ({navigate,cart,setCart,subscriptions=[],onTabChange,location=DEFAULT_LOCATION,addresses=[],setAddresses}) => {
  const [showAddrPicker,setShowAddrPicker]=useState(false);
  const cartCount=cart.items.reduce((a,i)=>a+i.qty,0);
  // Show the city's default delivery radius next to the location in the header.
  const cityRecord = findCity(location);
  const radiusKm = cityRecord ? cityRecord.radiusKm : 5;
  const activeAddress = addresses.find(a=>a.isDefault) || addresses[0] || null;
  const switchActiveTo = (addr) => {
    if (typeof setAddresses !== 'function') return;
    setAddresses(addrs => addrs.map(x => ({ ...x, isDefault: x.id === addr.id })));
    setShowAddrPicker(false);
  };
  const live=CATEGORIES.filter(c=>c.live);
  const IconBtn=({onClick,children,badge})=>(
    <button onClick={onClick} style={{position:'relative',width:40,height:40,borderRadius:R.lg,background:C.white,border:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',flexShrink:0,boxShadow:'0 1px 2px rgba(28,25,23,.04)'}}>
      {children}
      {badge}
    </button>
  );
  return (
    <div className="fade-in" style={{flex:1,display:'flex',flexDirection:'column'}}>
      <div style={{padding:'14px 16px 16px',background:'linear-gradient(160deg,#f1fbfd 0%,#d4f1f7 100%)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,gap:12}}>
          <button onClick={()=>setShowAddrPicker(true)} style={{display:'flex',alignItems:'center',gap:8,background:'none',border:'none',padding:0,cursor:'pointer',minWidth:0,flex:1,textAlign:'left'}}>
            <div style={{width:32,height:32,borderRadius:9,background:C.white,border:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0,lineHeight:1,boxShadow:'0 1px 2px rgba(28,25,23,.04)'}}>{activeAddress?tagEmoji(activeAddress.label):'📍'}</div>
            <div style={{minWidth:0,flex:1}}>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <span style={{fontSize:13,fontWeight:800,color:C.text,letterSpacing:-.1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{activeAddress?activeAddress.label:location}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{flexShrink:0}}><path d="M3 4l3 3 3-3" stroke={C.textMed} strokeWidth="1.6" strokeLinecap="round"/></svg>
              </div>
              <p style={{fontSize:11,color:C.textMed,marginTop:2,lineHeight:1.3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{activeAddress?activeAddress.full:`Within ${radiusKm} km · ${live.length} services active`}</p>
            </div>
          </button>
          <div style={{display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
            <IconBtn onClick={()=>navigate('notifications')} badge={<div style={{position:'absolute',top:-2,right:-2,width:10,height:10,borderRadius:5,background:C.primary,border:`2px solid #d4f1f7`}}/>}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2a6 6 0 016 6c0 2.8-1.4 4.8-2 6H6c-.6-1.2-2-3.2-2-6a6 6 0 016-6z" stroke={C.text} strokeWidth="1.6"/><path d="M8 16a2 2 0 004 0" stroke={C.text} strokeWidth="1.6"/></svg>
            </IconBtn>
            <IconBtn onClick={()=>navigate('cart')} badge={cartCount>0?(<div style={{position:'absolute',top:-4,right:-4,minWidth:18,height:18,padding:'0 5px',borderRadius:9,background:C.primary,color:C.white,fontSize:10,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',border:`2px solid #d4f1f7`,fontVariantNumeric:'tabular-nums'}}>{cartCount}</div>):null}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M2 2h2l1.4 7M6 13h10l2-7H5.4" stroke={C.text} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="8" cy="17" r="1.5" fill={C.text}/><circle cx="14" cy="17" r="1.5" fill={C.text}/></svg>
            </IconBtn>
          </div>
        </div>
        <button onClick={()=>navigate('search')} style={{width:'100%',display:'flex',alignItems:'center',gap:10,padding:'12px 14px',background:C.white,borderRadius:R.lg,border:`1px solid ${C.border}`,cursor:'pointer',textAlign:'left',boxShadow:'0 1px 2px rgba(28,25,23,.04)',minHeight:46}}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke={C.textMed} strokeWidth="1.6"/><path d="M11 11l3 3" stroke={C.textMed} strokeWidth="1.6" strokeLinecap="round"/></svg>
          <span style={{fontSize:13,color:C.textLight,letterSpacing:.05}}>Search groceries, salons and more…</span>
        </button>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'16px 16px 20px',display:'flex',flexDirection:'column',gap:20}}>
        {(() => {
          const activeSubs = subscriptions.filter(s => s.status === 'active');
          if (activeSubs.length === 0) {
            return <GetStartedCard onStart={()=>navigate('product-catalog')}/>;
          }
          const next = nextDelivery(activeSubs);
          if (!next) {
            return <GetStartedCard onStart={()=>navigate('product-catalog')}/>;
          }
          return <NextDeliveryCard delivery={next} count={next.count} onViewRoutine={()=>typeof onTabChange==='function'&&onTabChange('subscriptions')}/>;
        })()}
        <RoutineChips subs={subscriptions} navigate={navigate}/>
        <NewOnRekraftRail navigate={navigate}/>
        <YouMightLikeRail subscriptions={subscriptions} navigate={navigate}/>
        {cart.items.length>0&&(
          <button onClick={()=>navigate('cart')} style={{width:'100%',display:'flex',alignItems:'center',gap:12,padding:'12px 14px',background:C.white,border:`1px solid ${C.border}`,borderRadius:R.lg,cursor:'pointer',textAlign:'left',boxShadow:'0 1px 2px rgba(28,25,23,.04)',minHeight:60}}>
            <div style={{width:36,height:36,borderRadius:R.md,background:'#e6f7fb',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0,lineHeight:1}}>🛒</div>
            <div style={{flex:1,minWidth:0}}>
              <p style={{fontSize:13,fontWeight:800,color:C.text,letterSpacing:-.1}}>Cart waiting · {cartCount} item{cartCount>1?'s':''}</p>
              <p style={{fontSize:11,color:C.textMed,marginTop:2,lineHeight:1.3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>from {cart.branchName}</p>
            </div>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{flexShrink:0}}><path d="M7 4l6 6-6 6" stroke={C.textMed} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        )}
        <div>
          <SHead title="Subscribe & save"/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            {[
              { id:'daily',  category:'Dairy',      em1:'🥛', em2:'🍞', em3:'🥚', label:'Daily routine', sub:'Milk · Bread · Eggs', bg:'linear-gradient(135deg,#dbeafe,#fef3c7)'},
              { id:'weekly', category:'Vegetables', em1:'🥬', em2:'🍅', em3:'🌿', label:'Weekly stock',  sub:'Fruits · Vegetables', bg:'linear-gradient(135deg,#dcfce7,#f1fbfd)'},
            ].map(c=>(
              <button key={c.id} onClick={()=>navigate('product-catalog',{category:c.category})} style={{padding:'16px 14px',borderRadius:R.xl,border:`1px solid ${C.border}`,background:c.bg,cursor:'pointer',textAlign:'left',minHeight:120,display:'flex',flexDirection:'column',justifyContent:'space-between',boxShadow:'0 1px 2px rgba(28,25,23,.04)'}}>
                <div style={{display:'flex',gap:2}}>
                  <span style={{fontSize:26,lineHeight:1,filter:'drop-shadow(0 2px 4px rgba(0,0,0,.08))'}}>{c.em1}</span>
                  <span style={{fontSize:22,lineHeight:1,marginLeft:-4,marginTop:6,filter:'drop-shadow(0 2px 4px rgba(0,0,0,.08))'}}>{c.em2}</span>
                  <span style={{fontSize:18,lineHeight:1,marginLeft:-4,marginTop:12,filter:'drop-shadow(0 2px 4px rgba(0,0,0,.08))'}}>{c.em3}</span>
                </div>
                <div>
                  <p style={{fontSize:14,fontWeight:800,color:C.text,letterSpacing:-.2,lineHeight:1.25}}>{c.label}</p>
                  <p style={{fontSize:11,color:C.textMed,marginTop:3,lineHeight:1.3}}>{c.sub}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
        <FeaturedRow navigate={navigate}/>
        <NearbyServicesRow navigate={navigate}/>
      </div>
      <AddressPickerSheet
        open={showAddrPicker}
        onClose={()=>setShowAddrPicker(false)}
        addresses={addresses}
        onPick={switchActiveTo}
        onAddNew={()=>{ setShowAddrPicker(false); navigate('address-editor'); }}
      />
    </div>
  );
};

/* ── Category Feed ───────────────────────────────────────────── */
export const CategoryFeedScreen = ({navigate,goBack,category:cat,cart,setCart}) => {
  const [search,setSearch]=useState('');
  const [subCat,setSubCat]=useState('All');
  const allBranches=getBranches(cat);
  const branches=allBranches.filter(b=>{
    if(search&&!b.name.toLowerCase().includes(search.toLowerCase()))return false;
    if(subCat==='All')return true;
    if(cat.btype==='grocery')return b.products?.some(p=>p.cat===subCat);
    return b.type.toLowerCase().includes(subCat.toLowerCase());
  });
  const isSalon=cat.btype==='salons';
  const cartCount=cart.items.reduce((a,i)=>a+i.qty,0);
  const bColors=['#d4f1f7','#dbeafe','#dcfce7','#fce7f3'];

  if(!cat.live) return (
    <div className="fade-in" style={{flex:1,display:'flex',flexDirection:'column'}}>
      <BkHdr title={cat.label} onBack={goBack}/>
      <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:32,textAlign:'center'}}>
        <div style={{width:96,height:96,borderRadius:R.xxl,background:cat.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:48,marginBottom:20,lineHeight:1}}>{cat.em}</div>
        <h2 style={{fontSize:22,fontWeight:800,color:C.text,marginBottom:10,letterSpacing:-.3}}>{cat.label} — coming soon</h2>
        <p style={{fontSize:14,color:C.textMed,lineHeight:1.55,maxWidth:280}}>We're on-boarding {cat.label.toLowerCase()} suppliers near you. We'll notify you the moment it's live.</p>
        <button style={{marginTop:24,padding:'12px 24px',borderRadius:R.lg,border:`1.5px solid ${C.primary}`,background:'#e6f7fb',color:C.primary,fontSize:14,fontWeight:700,cursor:'pointer',minHeight:44}}>🔔 Notify me when live</button>
      </div>
    </div>
  );

  const CartBtn=()=>(
    <button onClick={()=>navigate('cart')} style={{position:'relative',width:40,height:40,borderRadius:R.lg,background:C.white,border:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',boxShadow:'0 1px 2px rgba(28,25,23,.04)'}}>
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M2 2h2l1.4 7M6 13h10l2-7H5.4" stroke={C.text} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><circle cx="8" cy="17" r="1.5" fill={C.text}/><circle cx="14" cy="17" r="1.5" fill={C.text}/></svg>
      {cartCount>0&&cart.branchId&&<div style={{position:'absolute',top:-4,right:-4,minWidth:16,height:16,padding:'0 4px',borderRadius:8,background:C.primary,color:C.white,fontSize:9,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',border:`2px solid ${C.bg}`,fontVariantNumeric:'tabular-nums'}}>{cartCount}</div>}
    </button>
  );

  return (
    <div className="fade-in" style={{flex:1,display:'flex',flexDirection:'column'}}>
      <BkHdr title={cat.label} onBack={goBack} right={!isSalon&&<CartBtn/>}/>
      <div style={{padding:'12px 16px 0',background:C.bg}}>
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:C.white,borderRadius:R.lg,border:`1.5px solid ${search?C.primary:C.border}`,marginBottom:cat.subCats?.length>1?12:14,transition:'border .15s',minHeight:42}}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke={C.textMed} strokeWidth="1.6"/><path d="M11 11l3 3" stroke={C.textMed} strokeWidth="1.6" strokeLinecap="round"/></svg>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={`Search ${cat.label.toLowerCase()}…`} style={{flex:1,border:'none',outline:'none',fontSize:13,color:C.text,background:'none'}}/>
          {search&&<button onClick={()=>setSearch('')} style={{background:'none',border:'none',color:C.textMed,fontSize:18,cursor:'pointer',lineHeight:1,padding:4,margin:-4}}>×</button>}
        </div>
        {cat.subCats?.length>1&&(
          <div style={{display:'flex',gap:8,overflowX:'auto',paddingBottom:12,margin:'0 -16px',padding:'0 16px 12px'}}>
            {cat.subCats.map(sc=><Chip key={sc} active={subCat===sc} onClick={()=>setSubCat(sc)}>{sc}</Chip>)}
          </div>
        )}
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'4px 16px 20px',display:'flex',flexDirection:'column',gap:12}}>
        {branches.length>0&&<p style={{fontSize:12,fontWeight:700,color:C.textMed,letterSpacing:.1,padding:'0 2px'}}>{branches.length} supplier{branches.length!==1?'s':''} near you</p>}
        {branches.map((b,i)=>(
          <Card key={b.id} onClick={()=>navigate('branch',{branch:b,btype:cat.btype})} style={{padding:0,overflow:'hidden',cursor:'pointer'}}>
            <div style={{height:120,background:`linear-gradient(135deg,${bColors[i%4]},${bColors[(i+2)%4]})`,position:'relative',overflow:'hidden'}}>
              <ImgOrEmoji src={D.storeImages[b.id]} fallback={b.em} size={120} radius={0} bg="transparent" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              <div style={{position:'absolute',top:10,right:10,display:'flex',gap:6,flexWrap:'wrap',justifyContent:'flex-end',maxWidth:'70%'}}>
                <Badge bg="rgba(255,255,255,.94)" color={C.text} sm>⏰ {b.hours}</Badge>
                {b.certified&&<Badge bg="rgba(220,252,231,.94)" color="#15803d" sm>✓ Certified</Badge>}
              </div>
            </div>
            <div style={{padding:'14px 16px 16px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8,gap:12}}>
                <div style={{minWidth:0,flex:1}}>
                  <p style={{fontSize:15,fontWeight:800,color:C.text,letterSpacing:-.2,lineHeight:1.25,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{b.name}</p>
                  <p style={{fontSize:12,color:C.textMed,marginTop:3,lineHeight:1.3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{b.type}</p>
                </div>
                <div style={{textAlign:'right',flexShrink:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:4,justifyContent:'flex-end'}}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="#d97706"><polygon points="6,1 7.5,4.5 11.5,5 8.5,7.5 9.5,11.5 6,9.5 2.5,11.5 3.5,7.5 0.5,5 4.5,4.5"/></svg>
                    <span style={{fontSize:13,fontWeight:800,color:C.text,fontVariantNumeric:'tabular-nums'}}>{b.rating}</span>
                    <span style={{fontSize:11,color:C.textMed,fontVariantNumeric:'tabular-nums'}}>({b.reviews})</span>
                  </div>
                  <span style={{display:'inline-flex',alignItems:'center',gap:3,background:'#f0fdf4',color:'#15803d',padding:'3px 8px',borderRadius:R.pill,fontSize:11,fontWeight:700,marginTop:6,fontVariantNumeric:'tabular-nums'}}>📍 {b.dist} km</span>
                </div>
              </div>
              <p style={{fontSize:12,color:C.textMed,letterSpacing:.05}}>{isSalon?`${b.services?.length} services`:`${b.products?.length} products`} available</p>
            </div>
          </Card>
        ))}
        {branches.length===0&&(
          <div style={{textAlign:'center',padding:'48px 20px'}}>
            <div style={{width:80,height:80,borderRadius:40,background:'#e6f7fb',display:'flex',alignItems:'center',justifyContent:'center',fontSize:36,marginBottom:14,marginLeft:'auto',marginRight:'auto'}}>🔍</div>
            <p style={{fontSize:15,fontWeight:800,color:C.text,letterSpacing:-.1}}>No {cat.label.toLowerCase()} suppliers found</p>
            <p style={{fontSize:13,color:C.textMed,marginTop:6,lineHeight:1.45}}>{search?'Try a different search term':'Check back soon'}</p>
          </div>
        )}
      </div>
      {!isSalon&&cart.branchId&&cart.items.length>0&&(
        <div style={{padding:'12px 16px 16px',background:C.white,borderTop:`1px solid ${C.border}`,boxShadow:SHADOW.bottomBar}}>
          <Btn full onClick={()=>navigate('cart')}>View cart · {cartCount} item{cartCount>1?'s':''}</Btn>
        </div>
      )}
    </div>
  );
};
